import { useEffect, useState, useMemo, useCallback } from "react";
import { RootState } from "../../store";
import { connect, ConnectedProps } from "react-redux";

import { approveUser, rejectUser } from "@/store/user/user.action";
import { CustomSelect, Option } from "..";
import { useFormik } from "formik";
import * as Yup from "yup";
import Avatar from "../../assets/icons/img/avatar/avatar.png";
import { SERVICES } from "../../constants";

interface NotificationProps {
  message: any;
  onClose?: any;
}

const UserRequest = ({
  message,
  bunchBusinessList,

  bunchProductList,
  approveUser,
  rejectUser,
  onClose,
}: Props) => {
  const [sampleListings, setSampleListings] = useState<any[]>([]);
  const [visibleCount, setVisibleCount] = useState(
    sampleListings.length > 6 ? 4 : sampleListings.length
  );

  useEffect(() => {
    setVisibleCount(sampleListings.length > 6 ? 4 : sampleListings.length);
  }, [sampleListings]);

  const handleLoadMore = useCallback(() => {
    setVisibleCount(sampleListings.length);
  }, [sampleListings.length]);

  const mapIdToName = useCallback(
    (id: number, type: string) => {
      if (type === "business") {
        return bunchBusinessList?.find((business: any) => business?.id === id)
          ?.name;
      } else if (type === "team") {
        return bunchProductList?.find((team: any) => team?.id === id)?.name;
      }
    },
    [bunchBusinessList, bunchProductList]
  );

  const formik = useFormik({
    initialValues: {
      login_type: 2,
      permissions: message.permissions || [],
    },
    validationSchema: Yup.object({
      login_type: Yup.string().required("Login type is required"),
    }),
    onSubmit: (values) => {
      // Handle form submission here
    },
  });

  const handleAccept = useCallback(() => {
    const permissionIds = sampleListings.map((listing) =>
      listing.id.toLowerCase()
    );
    if (formik.errors) {
      return;
    }
    approveUser(
      {
        id: message?.id,
        permissions: permissionIds,
        login_type: formik.values.login_type,
      },
      onClose
    );
  }, [
    sampleListings,
    formik.errors,
    formik.values.login_type,
    message?.id,
    approveUser,
    onClose,
  ]);

  const handleDecline = useCallback(() => {
    rejectUser({ id: message?.id }, onClose);
  }, [message?.id, rejectUser, onClose]);

  const permissionOptions = useMemo(() => {
    const services = SERVICES?.map((service: any) => service?.value);
    const options: Option[] = [];
    bunchBusinessList?.forEach((business: any) => {
      const businessName = business?.name;
      const businessId = business?.id;
      const productsForBusiness = bunchProductList?.filter(
        (product: any) => product?.business === businessId
      );
      productsForBusiness?.forEach((product: any) => {
        const productName = product?.name;
        const productId = product?.id;
        services?.forEach((service: string) => {
          options.push({
            label: `${businessName}-${productName}-${service}`,
            value: `${businessId}.${productId}.${service}`,
          });
        });
      });
    });
    return options;
  }, [bunchBusinessList, bunchProductList]);

  const getBasePermission = useCallback((permission: string): string => {
    const dotIndex = permission?.lastIndexOf(".");
    if (dotIndex !== -1) {
      return permission?.substring(0, dotIndex);
    } else {
      return permission;
    }
  }, []);

  const processPermissionForSelect = useCallback(
    (array: string[]) => {
      return array?.map((str) => {
        return getBasePermission(str);
      });
    },
    [getBasePermission]
  );

  const handlePermissionChange = useCallback(
    (values: string[]) => {
      const newPermissions = values?.map((value) => {
        const existingPermission = formik.values?.permissions?.find(
          (permission: string) => permission?.startsWith(value)
        );
        return existingPermission ? existingPermission : `${value}.r`;
      });
      formik.setFieldValue("permissions", newPermissions);
    },
    [formik.values?.permissions, formik.setFieldValue]
  );

  useEffect(() => {
    const transformedPermissions = formik.values?.permissions?.map(
      (permission: any, index: any) => ({
        id: permission,
        title: permission,
        isChecked: true,
        image: Avatar,
      })
    );
    setSampleListings(transformedPermissions);
  }, [formik.values?.permissions]);

  const loginModeOptions = useMemo(
    () => [
      { label: "Slike", value: 1 },
      { label: "Ad", value: 2 },
      { label: "Google", value: 3 },
    ],
    []
  );

  // /Users/nilaypran/node/slike-frontend/libs/shared-assets/icons/img/avatar
  return (
    <div>
      <div className="d-flex flex-row gap-4 mb-4 justify-content-between">
        <div className="d-flex flex-column gap-2">
          <div className="d-flex flex-row gap-3">
            <label>Name</label>
            <span>{message.name}</span>
          </div>
          <div className="d-flex flex-row gap-3">
            <label>Mobile Number</label>
            <span>{message.phone}</span>
          </div>
          <div className="d-flex flex-row gap-3">
            <label>team</label>
            <span>{mapIdToName(message.team, "team")}</span>
          </div>
        </div>
        <div className="d-flex flex-column gap-2">
          <div className="d-flex flex-row gap-3">
            <label>Email ID</label>
            <span>{message.email}</span>
          </div>
          <div className="d-flex flex-row gap-3">
            <label>Business</label>
            <span>{mapIdToName(message.business, "business")}</span>
          </div>
          <div className="d-flex flex-row gap-3">
            <label>Default team</label>
            <span>{mapIdToName(message.default_team, "team")}</span>
          </div>
        </div>
      </div>
      <hr></hr>
      <form onSubmit={formik.handleSubmit}>
        <div className="mb-3">
          <CustomSelect
            className="mb-3"
            value={formik.values.login_type}
            label="Login Mode"
            name="login_type"
            options={loginModeOptions}
            disabled={false}
            showRequired={true}
            placeholder="Login Mode"
            handleChange={(newValue: any) => {
              formik.setFieldValue("login_type", newValue.value);
            }}
          />
          {formik.touched.login_type && formik.errors.login_type ? (
            <div className="invalid-feedback">{formik.errors.login_type}</div>
          ) : null}
        </div>
        <div className="row mt-4">
          <div className="col-6 mt-3">
            <h6 className="me-2">ACCESS PERMISSION</h6>
          </div>
          <div className="col-6">
            <CustomSelect
              name="permissions"
              value={processPermissionForSelect(formik.values.permissions)}
              placeholder="Search teams"
              className="my-1 mb-1 flex-grow-1"
              multiSelect={true}
              options={permissionOptions}
              handleChange={(newValue: any) => {
                handlePermissionChange(
                  newValue.map((option: Option) => option.value)
                );
              }}
            />
          </div>
        </div>
        <div className="d-flex justify-content-center align-items-center position-relative">
          {sampleListings.length > 6 &&
            visibleCount < sampleListings.length && (
              <div
                className="load-more"
                style={{ cursor: "pointer" }}
                onClick={handleLoadMore}
              >
                <span className="bg-white px-3 py-1">Load More</span>
              </div>
            )}
        </div>
        <div className="d-flex justify-content-end mt-3">
          <button className="btn btn-primary-outline" onClick={handleDecline}>
            Decline
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleAccept}
          >
            Accept
          </button>
        </div>
      </form>
    </div>
  );
};
const mapState = (state: RootState) => ({
  bunchBusinessList: state.constants.bunchBusinessList.list,
  bunchProductList: state.constants.bunchProductList.list,
});

const mapDispatch = {
  approveUser,
  rejectUser,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & NotificationProps;

export default connector(UserRequest);
